import React, { useState } from 'react';
import { X, Check } from 'lucide-react';

const PermissionsModal = ({ user, onClose, onSubmit }) => {
  const [permissions, setPermissions] = useState(user.permissions || {
    agents: {},
    admin: {},
    features: {}
  });

  // Available agents (will be dynamically loaded in real implementation)
  const availableAgents = [
    { id: 'rfq_agent', name: 'RFQ Agent' },
    { id: 'bill_agent', name: 'Bill Agent' },
    { id: 'invoice_agent', name: 'Invoice Agent' }
  ];

  const adminPermissions = [
    { key: 'user_management', label: 'User Management' },
    { key: 'system_settings', label: 'System Settings' },
    { key: 'authentication', label: 'Authentication Management' },
    { key: 'audit_logs', label: 'View Audit Logs' }
  ];

  const features = [
    { key: 'mcp_chat', label: 'MCP Chat' },
    { key: 'monitoring', label: 'System Monitoring' },
    { key: 'workflow_builder', label: 'Workflow Builder' }
  ];

  const handleAgentPermissionChange = (agentId, permission, value) => {
    setPermissions(prev => ({
      ...prev,
      agents: {
        ...prev.agents,
        [agentId]: {
          ...prev.agents[agentId],
          [permission]: value
        }
      }
    }));
  };

  const handleAdminPermissionChange = (key, value) => {
    setPermissions(prev => ({
      ...prev,
      admin: {
        ...prev.admin,
        [key]: value
      }
    }));
  };

  const handleFeatureChange = (key, value) => {
    setPermissions(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(permissions);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold">Manage Permissions</h2>
            <p className="text-sm text-gray-600 mt-1">
              Configure permissions for {user.name} ({user.role})
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Agent Permissions */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Agent Permissions</h3>
            <div className="space-y-4">
              {availableAgents.map(agent => (
                <div key={agent.id} className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">{agent.name}</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={permissions.agents[agent.id]?.read || false}
                        onChange={(e) => handleAgentPermissionChange(agent.id, 'read', e.target.checked)}
                      />
                      <span className="text-sm">Read</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={permissions.agents[agent.id]?.write || false}
                        onChange={(e) => handleAgentPermissionChange(agent.id, 'write', e.target.checked)}
                      />
                      <span className="text-sm">Write</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={permissions.agents[agent.id]?.execute || false}
                        onChange={(e) => handleAgentPermissionChange(agent.id, 'execute', e.target.checked)}
                      />
                      <span className="text-sm">Execute</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Admin Permissions (only show for admin role) */}
          {user.role === 'admin' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Admin Permissions</h3>
              <div className="grid grid-cols-2 gap-4">
                {adminPermissions.map(perm => (
                  <label key={perm.key} className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={permissions.admin[perm.key] || false}
                      onChange={(e) => handleAdminPermissionChange(perm.key, e.target.checked)}
                    />
                    <span className="text-sm">{perm.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Feature Access */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Feature Access</h3>
            <div className="grid grid-cols-2 gap-4">
              {features.map(feature => (
                <label key={feature.key} className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={permissions.features[feature.key] || false}
                    onChange={(e) => handleFeatureChange(feature.key, e.target.checked)}
                  />
                  <span className="text-sm">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold mb-2">Quick Actions</h4>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  // Grant all permissions
                  const allPerms = { agents: {}, admin: {}, features: {} };
                  availableAgents.forEach(agent => {
                    allPerms.agents[agent.id] = { read: true, write: true, execute: true };
                  });
                  adminPermissions.forEach(perm => {
                    allPerms.admin[perm.key] = true;
                  });
                  features.forEach(feature => {
                    allPerms.features[feature.key] = true;
                  });
                  setPermissions(allPerms);
                }}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
              >
                Grant All
              </button>
              <button
                type="button"
                onClick={() => {
                  // Revoke all permissions
                  setPermissions({ agents: {}, admin: {}, features: {} });
                }}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Revoke All
              </button>
              <button
                type="button"
                onClick={() => {
                  // Set default permissions based on role
                  const defaultPerms = { agents: {}, admin: {}, features: {} };
                  if (user.role === 'admin') {
                    availableAgents.forEach(agent => {
                      defaultPerms.agents[agent.id] = { read: true, write: true, execute: true };
                    });
                    adminPermissions.forEach(perm => {
                      defaultPerms.admin[perm.key] = true;
                    });
                    features.forEach(feature => {
                      defaultPerms.features[feature.key] = true;
                    });
                  } else if (user.role === 'customer') {
                    availableAgents.forEach(agent => {
                      defaultPerms.agents[agent.id] = { read: true, write: true, execute: true };
                    });
                    defaultPerms.features.mcp_chat = true;
                  } else {
                    availableAgents.forEach(agent => {
                      defaultPerms.agents[agent.id] = { read: true, write: false, execute: false };
                    });
                  }
                  setPermissions(defaultPerms);
                }}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Set Role Defaults
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Permissions
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PermissionsModal;